import { makeid } from 'utils/helpers/makeid';

import type { TFormItem } from '../../types';

const yesNo = (name: string, label: string): TFormItem => ({
  key: makeid(10),
  name,
  type: 'radio',
  title: label,
  required: true,
  options: [
    { key: makeid(10), label: 'Yes', value: 'yes' },
    { key: makeid(10), label: 'No', value: 'no' },
  ],
});

export const hypertensionChecklist: TFormItem[] = [
  yesNo('sym_htn_headache', 'Do you have a severe headache?'),
  yesNo('sym_htn_chestpain', 'Do you have chest pain or pressure?'),
  yesNo('sym_htn_sob', 'Are you short of breath?'),
  yesNo(
    'sym_htn_neuro',
    'Do you have any vision changes, numbness, or weakness on one side?',
  ),
];

export const hypotensionChecklist: TFormItem[] = [
  yesNo('sym_hypo_dizzy', 'Are you feeling dizzy or lightheaded?'),
  yesNo('sym_hypo_faint', 'Have you fainted or nearly fainted?'),
  yesNo('sym_hypo_confused', 'Do you feel confused?'),
];

export const tachycardiaChecklist: TFormItem[] = [
  yesNo('sym_tachy_chestpain', 'Do you have chest pain?'),
  yesNo('sym_tachy_sob', 'Are you short of breath?'),
  yesNo('sym_tachy_dizzy', 'Are you feeling dizzy, lightheaded, or confused?'),
];

export const bradycardiaChecklist: TFormItem[] = [
  yesNo('sym_brady_dizzy', 'Are you feeling dizzy or lightheaded?'),
  yesNo('sym_brady_faint', 'Have you fainted or nearly fainted?'),
  yesNo('sym_brady_chestpain', 'Do you have chest pain?'),
  yesNo('sym_brady_sob', 'Are you short of breath?'),
];

export const hypoxiaChecklist: TFormItem[] = [
  yesNo('sym_hypoxia_sob', 'Are you short of breath at rest?'),
  yesNo('sym_hypoxia_cyanosis', 'Do your lips or nails look blue or gray?'),
  yesNo('sym_hypoxia_confused', 'Do you feel confused?'),
  yesNo('sym_hypoxia_speak', 'Can you speak in full sentences right now?'),
];

export const hypoglycemiaChecklist: TFormItem[] = [
  yesNo('sym_hypogly_shaky', 'Are you shaky or sweaty?'),
  yesNo(
    'sym_hypogly_confused',
    'Do you feel confused or have trouble speaking?',
  ),
  yesNo('sym_hypogly_vision', 'Is your vision blurry?'),
  yesNo('sym_hypogly_faint', 'Have you felt like you might pass out?'),
];

export const hyperglycemiaChecklist: TFormItem[] = [
  yesNo(
    'sym_hypergly_thirst',
    'Are you unusually thirsty or urinating more than usual?',
  ),
  yesNo('sym_hypergly_nausea', 'Do you feel nauseous, or have you vomited?'),
  yesNo('sym_hypergly_stomach', 'Do you have stomach pain?'),
  yesNo('sym_hypergly_confused', 'Do you feel confused?'),
];

export const respiratoryDistressChecklist: TFormItem[] = [
  yesNo('sym_resp_sob', 'Are you very short of breath right now?'),
  yesNo('sym_resp_speak', 'Can you speak in full sentences?'),
  yesNo('sym_resp_cyanosis', 'Do your lips or nails look blue?'),
  yesNo('sym_resp_inhaler', 'Does your rescue inhaler help?'),
];

export const feverChecklist: TFormItem[] = [
  yesNo('sym_fever_confused', 'Do you feel confused?'),
  yesNo('sym_fever_neck', 'Do you have a stiff neck?'),
  yesNo('sym_fever_breathing', 'Are you having trouble breathing?'),
  yesNo(
    'sym_fever_rash',
    'Do you have a new rash, or does your skin look mottled or discolored?',
  ),
];

export const hypothermiaChecklist: TFormItem[] = [
  yesNo('sym_hypotherm_drowsy', 'Do you feel confused or very drowsy?'),
  yesNo('sym_hypotherm_speech', 'Is your speech slurred?'),
  yesNo('sym_hypotherm_shiver', 'Have you stopped shivering?'),
  yesNo('sym_hypotherm_pulse', 'Does your pulse feel very weak or faint?'),
];

/**
 * Short symptom names keyed by field name, used to condense the generated note
 * (e.g. "Severe headache, Chest pain, ...") instead of listing each full question.
 */
export const SYMPTOM_SHORT_NAMES: Record<string, string> = {
  sym_htn_headache: 'Severe headache',
  sym_htn_chestpain: 'Chest pain/pressure',
  sym_htn_sob: 'Shortness of breath',
  sym_htn_neuro: 'Vision changes/numbness/weakness',
  sym_hypo_dizzy: 'Dizziness/lightheadedness',
  sym_hypo_faint: 'Fainting/near-fainting',
  sym_hypo_confused: 'Confusion',
  sym_tachy_chestpain: 'Chest pain',
  sym_tachy_sob: 'Shortness of breath',
  sym_tachy_dizzy: 'Dizziness/lightheadedness/confusion',
  sym_brady_dizzy: 'Dizziness/lightheadedness',
  sym_brady_faint: 'Fainting/near-fainting',
  sym_brady_chestpain: 'Chest pain',
  sym_brady_sob: 'Shortness of breath',
  sym_hypoxia_sob: 'Shortness of breath at rest',
  sym_hypoxia_cyanosis: 'Blue/gray lips or nails',
  sym_hypoxia_confused: 'Confusion',
  sym_hypoxia_speak: 'Able to speak in full sentences',
  sym_hypogly_shaky: 'Shakiness/sweating',
  sym_hypogly_confused: 'Confusion/trouble speaking',
  sym_hypogly_vision: 'Blurry vision',
  sym_hypogly_faint: 'Feeling faint',
  sym_hypergly_thirst: 'Excessive thirst/urination',
  sym_hypergly_nausea: 'Nausea/vomiting',
  sym_hypergly_stomach: 'Stomach pain',
  sym_hypergly_confused: 'Confusion',
  sym_resp_sob: 'Severe shortness of breath',
  sym_resp_speak: 'Able to speak in full sentences',
  sym_resp_cyanosis: 'Blue lips or nails',
  sym_resp_inhaler: 'Rescue inhaler helps',
  sym_fever_confused: 'Confusion',
  sym_fever_neck: 'Stiff neck',
  sym_fever_breathing: 'Trouble breathing',
  sym_fever_rash: 'New rash/mottled skin',
  sym_hypotherm_drowsy: 'Confusion/drowsiness',
  sym_hypotherm_speech: 'Slurred speech',
  sym_hypotherm_shiver: 'Stopped shivering',
  sym_hypotherm_pulse: 'Weak/faint pulse',
};
